import { Crosshair, Check, X } from 'lucide-react';
import { STATION_SPECS } from '@/data/stations';
import type { BuildMode } from '@/state/types';

interface MobileBuildOverlayProps {
  buildMode: BuildMode;
  /** Confirm building at the current map center. */
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Mobile-only overlay during build mode. Renders a centered crosshair plus
 * a "Hier bauen" CTA at the bottom — touchscreens can't tap-to-place
 * accurately enough for sub-50 m precision, so we move the camera and confirm.
 */
export function MobileBuildOverlay({ buildMode, onConfirm, onCancel }: MobileBuildOverlayProps) {
  if (!buildMode) return null;
  const spec = STATION_SPECS[buildMode.stationKind];

  return (
    <>
      {/* Center crosshair (pointer-events-none so map can be panned) */}
      <div
        aria-hidden
        className="pointer-events-none fixed left-1/2 top-1/2 z-30 -translate-x-1/2 -translate-y-1/2"
      >
        <span className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-accent-dispatch/80 bg-accent-dispatch/10 backdrop-blur-sm">
          <Crosshair className="h-8 w-8 text-accent-dispatch" aria-hidden />
        </span>
      </div>

      {/* Bottom action bar */}
      <div
        className="pointer-events-auto fixed bottom-24 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/10 px-2 py-2 shadow-2xl backdrop-blur-md"
        style={{ background: 'rgba(20, 24, 36, 0.92)' }}
      >
        <button
          type="button"
          onClick={onCancel}
          aria-label="Bau-Modus abbrechen"
          className="flex h-12 min-w-[48px] items-center justify-center rounded-full bg-bg-elevated text-fg-secondary transition hover:text-fg-primary"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
        <div className="flex flex-col px-3 leading-tight">
          <span className="text-[10px] uppercase tracking-wider text-fg-muted">Platzieren</span>
          <span className="font-display text-sm font-bold text-fg-primary">{spec.label}</span>
        </div>
        <button
          type="button"
          onClick={onConfirm}
          className="flex h-12 min-w-[48px] items-center justify-center gap-1.5 rounded-full bg-accent-emergency px-4 text-sm font-semibold text-white shadow-lg transition hover:bg-accent-emergency-hover"
        >
          <Check className="h-4 w-4" aria-hidden />
          Hier bauen
        </button>
      </div>
    </>
  );
}
